import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api/client.js';
import { TableSkeleton, Empty, ErrorBanner, Badge, SectionHead } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { relativeTime } from '../../lib/format.js';

/**
 * Who changed what, and when.
 *
 * Read-only by design — there is no edit or delete route for this anywhere in
 * the API. A log an operator can amend is evidence of nothing.
 */

interface AuditRow {
  id: string; at: string; action: string;
  resource_type: string; resource_id: string | null;
  actor_type: string; actor: string;
  impersonated_by: string | null; impersonated_by_email: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  reason: string | null; result: string;
  ip: string | null; request_id: string | null;
}

const TONE: Record<string, 'ok' | 'warn' | 'danger' | 'info' | 'muted'> = {
  created: 'ok', updated: 'info', deleted: 'danger',
  accepted: 'ok', applied: 'warn',
};

/** The verb after the last dot is what a reader actually scans for. */
function toneFor(action: string): 'ok' | 'warn' | 'danger' | 'info' | 'muted' {
  const verb = action.split('.').pop() ?? '';
  return TONE[verb] ?? 'muted';
}

export function AuditTab({ tenantId }: { tenantId: string }) {
  const [days, setDays] = useState(30);
  const [action, setAction] = useState('');
  const [actor, setActor] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ['audit', tenantId, days, action, actor],
    queryFn: () => api.get<{
      data: AuditRow[]; actions: Array<{ action: string; n: number }>;
    }>(`/v1/tenants/${tenantId}/audit?days=${days}` +
       `${action ? `&action=${encodeURIComponent(action)}` : ''}` +
       `${actor ? `&actor=${encodeURIComponent(actor)}` : ''}`),
    enabled: !!tenantId,
  });

  const rows = q.data?.data ?? [];

  return (
    <>
      <SectionHead title="Audit log"
        sub="Every change to this account, who made it and from where." />

      {q.error && <ErrorBanner error={q.error} />}

      <div className="arc-card">
        <div className="arc-toolbar">
          <div className="arc-search">
            <Icon name="search" size={14} />
            <input className="arc-input" placeholder="Search by person"
              value={actor} onChange={(e) => setActor(e.target.value)}
              aria-label="Search by person" />
          </div>
          <select className="arc-select" style={{ maxWidth: 220 }} value={action}
            aria-label="Filter by action"
            onChange={(e) => setAction(e.target.value)}>
            <option value="">All actions</option>
            {/* Offered from what is actually recorded, so the filter cannot
                drift from reality the way a hardcoded list does. */}
            {q.data?.actions.map((a) => (
              <option key={a.action} value={a.action}>{a.action} ({a.n})</option>
            ))}
          </select>
          <select className="arc-select" style={{ maxWidth: 130 }} value={days}
            aria-label="Time range"
            onChange={(e) => setDays(Number(e.target.value))}>
            <option value={1}>Last 24 hours</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last year</option>
          </select>
          <div className="arc-spacer" />
          <span className="arc-subtle arc-num" style={{ fontSize: 12.5 }}>
            {rows.length} entr{rows.length === 1 ? 'y' : 'ies'}
          </span>
        </div>

        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                <th>When</th><th>Who</th><th>Action</th>
                <th>Resource</th><th>From</th><th />
              </tr>
            </thead>
            {q.data === undefined ? <TableSkeleton query={q} cols={6} /> : (
              <tbody>
                {rows.map((r) => (
                  <>
                    <tr key={r.id} className="arc-clickable"
                      onClick={() => setExpanded(expanded === r.id ? null : r.id)}>
                      <td className="arc-muted" title={new Date(r.at).toLocaleString('en-ZA')}>
                        {relativeTime(r.at)}
                      </td>
                      <td>
                        <div className="arc-stack">
                          <span style={{ fontWeight: 550 }}>{r.actor}</span>
                          {/* Impersonation has to be visible: "the customer
                              changed it" and "we changed it as the customer"
                              are different answers to the same question. */}
                          {r.impersonated_by && (
                            <span className="arc-subtle" style={{ fontSize: 11 }}>
                              impersonated by {r.impersonated_by_email ?? r.impersonated_by}
                            </span>
                          )}
                        </div>
                      </td>
                      <td><Badge tone={toneFor(r.action)}>{r.action}</Badge></td>
                      <td className="arc-muted">
                        {r.resource_type}
                        {r.resource_id && (
                          <span className="arc-mono arc-subtle" style={{ marginLeft: 6, fontSize: 11 }}>
                            {r.resource_id.slice(0, 8)}
                          </span>
                        )}
                      </td>
                      <td className="arc-mono arc-muted" style={{ fontSize: 11.5 }}>
                        {r.ip ?? <span className="arc-subtle">—</span>}
                      </td>
                      <td className="arc-td-right">
                        {(r.before || r.after || r.reason) && (
                          <Icon name={expanded === r.id ? 'chevronDown' : 'chevronRight'} size={13} />
                        )}
                      </td>
                    </tr>
                    {expanded === r.id && (
                      <tr key={`${r.id}-detail`}>
                        <td colSpan={6} style={{ background: 'var(--arc-surface-2)' }}>
                          {r.reason && (
                            <p className="arc-muted" style={{ fontSize: 12.5, marginTop: 0 }}>
                              {r.reason}
                            </p>
                          )}
                          {(r.before || r.after) && (
                            <div className="arc-grid arc-grid-2">
                              <div>
                                <div className="arc-label">Before</div>
                                <pre className="arc-diff">{fmt(r.before)}</pre>
                              </div>
                              <div>
                                <div className="arc-label">After</div>
                                <pre className="arc-diff">{fmt(r.after)}</pre>
                              </div>
                            </div>
                          )}
                          {r.request_id && (
                            <p className="arc-subtle" style={{ fontSize: 11, marginBottom: 0 }}>
                              request {r.request_id}
                            </p>
                          )}
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            )}
          </table>
        </div>

        {q.data !== undefined && rows.length === 0 && (
          <Empty icon="activity" title="Nothing recorded in this window"
            hint="Changes to extensions, policies and account settings appear here." />
        )}
      </div>
    </>
  );
}

function fmt(v: Record<string, unknown> | null): string {
  if (!v || Object.keys(v).length === 0) return '—';
  return Object.entries(v)
    .map(([k, val]) => `${k}: ${typeof val === 'object' ? JSON.stringify(val) : String(val)}`)
    .join('\n');
}
